import React from "react";
import { useTranslation } from "react-i18next";
import { Lightbulb, Sparkles, Video } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { formatRelativeTime } from "@/components/inbox/utils";
import { formatDuration } from "./utils";
import type { TranscriptListItem as TranscriptListItemData } from "@/types/transcript";

export interface TranscriptListItemProps {
	transcript: TranscriptListItemData;
	selected: boolean;
	onSelect: (id: string) => void;
}

/**
 * One scannable transcript row: subject · relative time on the first line,
 * duration · participants and linked-candidate badges below, with AI
 * summary/insight presence indicators.
 */
export const TranscriptListItem: React.FC<TranscriptListItemProps> = React.memo(
	({ transcript, selected, onSelect }) => {
		const { t, i18n } = useTranslation();

		const time = formatRelativeTime(
			transcript.start_time || undefined,
			i18n.language,
			t("inbox.yesterday", "Yesterday")
		);
		const duration = formatDuration(transcript.start_time, transcript.end_time);
		const participantCount = transcript.participants?.length || 0;
		const hasInsights = !!transcript.insights && Object.keys(transcript.insights).length > 0;
		const linked = transcript.linked_candidates || [];

		return (
			<li role="option" aria-selected={selected}>
				<button
					type="button"
					onClick={() => onSelect(transcript.id)}
					className={cn(
						"relative flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors",
						"focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
						selected ? "bg-accent" : "hover:bg-muted/60"
					)}
				>
					{selected && <span className="absolute inset-y-1 left-0 w-0.5 rounded-full bg-primary" />}
					<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-muted/30">
						<Video className="h-4 w-4 text-muted-foreground" aria-hidden />
					</div>
					<div className="min-w-0 flex-1">
						<div className="flex items-baseline justify-between gap-2">
							<span className="truncate text-sm font-medium text-foreground">
								{transcript.subject || t("transcripts.untitled", "Untitled Meeting")}
							</span>
							<span className="shrink-0 text-xs tabular-nums text-muted-foreground">{time}</span>
						</div>
						<div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
							<span className="truncate">
								{[duration, participantCount > 0 ? t("transcripts.participantCount", "{{count}} participants", { count: participantCount }) : ""]
									.filter(Boolean)
									.join(" · ") || transcript.organizer_email || " "}
							</span>
							{transcript.summary && (
								<Tooltip>
									<TooltipTrigger asChild>
										<span className="ml-auto shrink-0">
											<Sparkles className="h-3 w-3" aria-hidden />
										</span>
									</TooltipTrigger>
									<TooltipContent side="left">{t("transcripts.tabs.aiSummary", "AI Summary")}</TooltipContent>
								</Tooltip>
							)}
							{hasInsights && (
								<Tooltip>
									<TooltipTrigger asChild>
										<span className={cn("shrink-0", !transcript.summary && "ml-auto")}>
											<Lightbulb className="h-3 w-3" aria-hidden />
										</span>
									</TooltipTrigger>
									<TooltipContent side="left">{t("transcripts.tabs.insights", "Insights")}</TooltipContent>
								</Tooltip>
							)}
						</div>
						{linked.length > 0 && (
							<div className="mt-1 flex flex-wrap items-center gap-1">
								{linked.slice(0, 2).map((candidate) => (
									<Badge key={candidate.id} variant="outline" className="text-xs">
										{candidate.name}
									</Badge>
								))}
								{linked.length > 2 && (
									<Badge variant="outline" className="text-xs">
										+{linked.length - 2}
									</Badge>
								)}
							</div>
						)}
					</div>
				</button>
			</li>
		);
	}
);

TranscriptListItem.displayName = "TranscriptListItem";
